import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { setStoredToken } from '@/lib/auth';
import { flushAttribution } from '@/lib/attribution';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AuthCallback = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get('session_id');

      if (!sessionId) {
        console.warn('[AuthCallback] No session_id in URL hash, redirecting to /');
        navigate('/');
        return;
      }

      console.info('[AuthCallback] Exchanging session_id with backend...', {
        backend: BACKEND_URL,
        sessionIdPrefix: sessionId.substring(0, 8) + '...',
      });

      try {
        const response = await axios.post(
          `${BACKEND_URL}/api/auth/session`,
          {},
          {
            headers: { 'X-Session-ID': sessionId },
            withCredentials: true,
          }
        );
        console.info('[AuthCallback] Auth success, navigating to /dashboard');
        const { user, session_token } = response.data;
        // Store token in localStorage as primary auth mechanism — cookies are unreliable
        // cross-domain (CHIPS partitioning, Safari ITP, etc.). Bearer header is rock-solid.
        if (session_token) {
          setStoredToken(session_token);
        }
        flushAttribution();
        navigate('/dashboard', { state: { user }, replace: true });
      } catch (err) {
        // Surface the real error instead of silent redirect — helps diagnose production issues.
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail || err?.message || t('authcb.unknown_error');
        const networkErr = !err?.response;
        console.error('[AuthCallback] Auth failed:', {
          status,
          detail,
          networkErr,
          backend: BACKEND_URL,
          fullError: err,
        });
        setError({
          status: status || 'NETWORK',
          detail,
          networkErr,
          backend: BACKEND_URL,
        });
      }
    };

    processSession();
  }, [navigate, t]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6" data-testid="auth-callback-error">
        <div className="max-w-lg w-full p-6 bg-[#121212] border border-red-500/30 rounded-2xl">
          <h2 className="text-xl font-[Outfit] font-bold text-red-400 mb-2">{t('authcb.fail_title')}</h2>
          <p className="text-sm text-white/70 mb-4">
            {t('authcb.fail_body')}
          </p>
          <div className="p-3 bg-[#0A0A0A] border border-white/10 rounded text-xs text-white/80 space-y-1 font-mono">
            <div><span className="text-white/40">Status:</span> {String(error.status)}</div>
            <div><span className="text-white/40">Detail:</span> {String(error.detail)}</div>
            {error.networkErr && <div className="text-amber-400">{t('authcb.network_err')}</div>}
            <div className="break-all"><span className="text-white/40">Backend URL:</span> {error.backend}</div>
          </div>
          <div className="mt-4 flex gap-2">
            <button
              onClick={() => navigate('/')}
              data-testid="auth-error-back-btn"
              className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded text-sm transition"
            >
              {t('authcb.back_home')}
            </button>
            <button
              onClick={() => { hasProcessed.current = false; setError(null); window.location.reload(); }}
              data-testid="auth-error-retry-btn"
              className="px-4 py-2 bg-[#2563EB] hover:bg-[#3B82F6] rounded text-sm font-semibold transition"
            >
              {t('authcb.retry')}
            </button>
          </div>
          <p className="mt-3 text-[11px] text-white/40">
            {t('authcb.console_hint')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center" data-testid="auth-callback-loading">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-white/60">{t('authcb.authenticating')}</p>
      </div>
    </div>
  );
};

export default AuthCallback;
